import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bookmark, Trash2, ExternalLink, Clock, Newspaper, BookmarkX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import SEO from '@/components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_COLORS = {
  labour: 'bg-red-500',
  conservative: 'bg-blue-600',
  libdem: 'bg-orange-500',
  snp: 'bg-yellow-500',
  reform: 'bg-cyan-500',
  green: 'bg-green-500',
  plaid: 'bg-emerald-600',
  dup: 'bg-red-800',
  sinnfein: 'bg-green-700'
};

const PARTY_LABELS = {
  labour: 'Labour',
  conservative: 'Conservative',
  libdem: 'Lib Dem',
  snp: 'SNP',
  reform: 'Reform UK',
  green: 'Green',
  plaid: 'Plaid Cymru',
  dup: 'DUP',
  sinnfein: 'Sinn Féin'
};

export default function ReadingListPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookmarks();
  }, []);

  const loadBookmarks = async () => {
    try {
      const bookmarkIds = JSON.parse(localStorage.getItem('watchtower_bookmarks') || '[]');
      const bookmarkDetails = JSON.parse(localStorage.getItem('watchtower_bookmark_details') || '{}');
      
      if (bookmarkIds.length === 0) {
        setArticles([]);
        setLoading(false);
        return;
      }

      // Fetch full article details for each bookmark
      const fetchedArticles = await Promise.all(
        bookmarkIds.map(async (id) => {
          try {
            const res = await axios.get(`${API}/articles/${id}`);
            return res.data;
          } catch (error) {
            // If article is not found, use cached details
            if (bookmarkDetails[id]) {
              return { id, ...bookmarkDetails[id], notFound: true };
            }
            return null;
          }
        })
      );

      setArticles(fetchedArticles.filter(Boolean));
    } catch (error) {
      console.error('Failed to load bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = (articleId) => {
    const bookmarkIds = JSON.parse(localStorage.getItem('watchtower_bookmarks') || '[]');
    const updated = bookmarkIds.filter(id => id !== articleId);
    localStorage.setItem('watchtower_bookmarks', JSON.stringify(updated));
    
    // Also remove from details
    const bookmarkDetails = JSON.parse(localStorage.getItem('watchtower_bookmark_details') || '{}');
    delete bookmarkDetails[articleId];
    localStorage.setItem('watchtower_bookmark_details', JSON.stringify(bookmarkDetails));
    
    setArticles(articles.filter(a => a.id !== articleId && a._id !== articleId));
    toast.success('Removed from reading list');
  };

  const clearAll = () => {
    localStorage.setItem('watchtower_bookmarks', '[]');
    localStorage.setItem('watchtower_bookmark_details', '{}');
    setArticles([]);
    toast.success('Reading list cleared');
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-100 h-24 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-20" data-testid="reading-list-page">
      <SEO 
        title="Reading List"
        description="Your saved articles from The Watchtower"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/10 rounded-xl">
                <Bookmark className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black">Reading List</h1>
                <p className="text-slate-300">{articles.length} saved {articles.length === 1 ? 'article' : 'articles'}</p>
              </div>
            </div>
            
            {articles.length > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearAll}
                className="bg-white/10 border-white/20 text-white hover:bg-red-500 hover:border-red-500"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </div>
      </section>
      
      <div className="max-w-4xl mx-auto px-4 py-8">

        {articles.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <BookmarkX className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-700 mb-2">No saved articles</h2>
            <p className="text-slate-500 mb-6">
              Articles you save will appear here for easy access
            </p>
            <Link to="/">
              <Button className="bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 font-bold shadow-lg">
                Browse Articles
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article) => {
              const articleId = article.id || article._id;
              return (
                <div 
                  key={articleId}
                  className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:border-slate-300 transition-all group"
                >
                  <div className="flex gap-4">
                    <div className={`w-1 rounded-full flex-shrink-0 ${PARTY_COLORS[article.party] || 'bg-slate-400'}`} />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                          {PARTY_LABELS[article.party] || article.party}
                        </span>
                        {article.source_name && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Newspaper className="h-3 w-3" />
                            {article.source_name}
                          </span>
                        )}
                      </div>
                      
                      <Link 
                        to={`/article/${articleId}`}
                        className="block group-hover:text-blue-600 transition-colors"
                      >
                        <h3 className="font-semibold text-slate-900 line-clamp-2 mb-2">
                          {article.title}
                        </h3>
                      </Link>
                      
                      {article.published_date && (
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(new Date(article.published_date), 'dd MMM yyyy')}
                        </p>
                      )}
                      
                      {article.notFound && (
                        <p className="text-xs text-amber-600 mt-2">
                          This article may no longer be available
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <Link to={`/article/${articleId}`}>
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeBookmark(articleId)}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
